<script setup lang="ts">
import Button from "primevue/button";
import Tag from "primevue/tag";
import { useConfirm } from "primevue/useconfirm";
import ProjectInformation from "./ProjectInformation.vue";
import DirectLinkEditDialog from "./directlink/DirectLinkEditDialog.vue";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import {
    Department,
    DirectInput,
    DirectLink,
    Participant,
    ProductLanguages,
    Project,
    Trainer,
} from "../../types/PersologOnlineTypes.ts";
import ProjectConfig from "./configure-tabs/ProjectConfig.vue";
import { ref, watch, PropType, computed, onMounted, ComputedRef } from "vue";
import { isMobileView } from "../../utils/GeneralUtils.ts";
import ProjectDetailsEditMenu from "./ProjectDetailsEditMenu.vue";
import { storeToRefs } from "pinia";
import { useProjectDetailsDirectLinkStore } from "../../stores/projectDetailsDirectLink";
import { productHasBenchmarkDiagram } from "../../utils/ProjectUtils.ts";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    directInput: {
        type: Object as PropType<DirectInput | null>,
        default: null,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    languages: {
        type: Object as PropType<ProductLanguages | null>,
        default: null,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    departments: {
        type: Array as PropType<Department[]>,
        default: () => [],
    },
    benchmarkRangeValues: {
        type: Array as PropType<number[][]>,
        default: () => [12.5, 87.5, 12.5, 87.5],
    },
    disableBenchmarkEditButton: {
        type: Boolean,
        default: false,
    },
});

const editMode = ref(false);
const hasActiveParticipants = ref(false);
const valid = ref(true);
const updatedProject = ref<Project>(props.project);

// clone project without reactivity for editing purpose
const projectToEdit: ComputedRef<Project> = computed(() => {
    return {
        ...props.project,
        name: props.project?.name || "",
        product: props.project?.product || "",
        defaultProduct: props.project?.defaultProduct || "",
    };
});

function updateValid(isValid: boolean) {
    valid.value = isValid;
}

/**
 * Updates updatedProject with the new values obtained from the ProjectConfig component, which is later used to update the project in updateProject()
 * @param project - temporary project object retrieved from the ProjectConfig component
 */
function updateFromConfig(project: Project) {
    updatedProject.value = project;
}

const product = computed(() => ({
    productNo: props.project?.product || "",
    quantity: 0,
    profileLanguages: props.languages ? props.languages[props.project.product] : [],
    questionnaireLanguages: [],
    modelName: "",
    modelNo: "",
    rank: 0,
    productName: "",
    shopLink: "",
}));

const emit = defineEmits(["update:deleteProject", "update:project", "update:rangeValues", "update:benchmarkEditable"]);

const directLinkStore = useProjectDetailsDirectLinkStore();
const {
    directLinkCreationVisible,
    createDirectLinkWarningDialogVisible,
    availableQuantity,
    directLinkAmount,
} = storeToRefs(directLinkStore);

/**
 * Menu items for the project details split button
 * @type {MenuItem[]}
 * @property {string} name - unique name for each menu item following pattern: <action><Item>
 * @property {boolean} visible - visibility of the menu item
 * @property {string} label - label appearing on the menu item
 * @property {() => void} command - function to be executed on click
 * @property {boolean} disabled - whether the menu item is disabled
 */
/**
 * Benchmark products (in120/in121/in122) cannot run on a direct link: the link would reserve
 * contingent from the report product instead of the candidates' questionnaire product, and the
 * questionnaire start page resolves texts and languages from the report product, which has no
 * questionnaire. Direct links for them are tracked separately (RDT-31).
 */
const supportsDirectLink = () => !productHasBenchmarkDiagram(props.project.product);

const projectMenuItems = [
    {
        name: "createLinkItem",
        visible: props.directLink === null && supportsDirectLink(),
        label: t("project-configuration.directLink.create"),
        icon: "pi pi-link",
        command: () => {
            directLinkStore.openCreateDialog(isDirectLinkAllowed(), props.trainer.id, props.project.defaultProduct);
        },
        disabled: false,
    },
    {
        name: "editDirectLinkContingentItem",
        visible: props.directLink !== null,
        label: t("project-configuration.tabs.configureDirectLink"),
        icon: "pi pi-link",
        command: () => {
            directLinkStore.openEditDialog(props.trainer.id, props.project.defaultProduct);
        },
    },
    {
        name: "deleteLinkItem",
        visible: props.directLink !== null,
        label: t("project-configuration.deleteDirectLink"),
        icon: "pi pi-trash",
        command: () => {
            directLinkStore.openDeleteWarning();
        },
    },
    {
        name: "deleteProjectItem",
        visible: true,
        label: t("buttons.deleteProject"),
        icon: "pi pi-times",
        command: () => {
            deleteProjectWithConfirmation();
        },
    },
];

/**
 * Checks if direct link creation is allowed
 * @returns {boolean} - true if direct link creation is allowed
 */
const isDirectLinkAllowed = () => {
    return props.participants.every((user) => user.status != "CREATED");
};

/**
 * Checks if the anonymous switch needs to be disabled
 * Condition: once participants exist, anonymity cannot be enabled anymore; an already anonymous
 * project can only drop anonymity while no participant advanced beyond ACTIVATED.
 * @returns {boolean} - true if the anonymous switch needs to be disabled
 */
const checkAnonymousSwitchDisabled = () => {
    if (props.participants.length === 0) {
        return false;
    }
    // With participants present, anonymity can never be turned on (the backend rejects it too) —
    // and an anonymous project can only drop it while nobody progressed beyond activation.
    return (
        !props.project.anonymous ||
        !props.participants.every((user) => user.status === "ACTIVATED" || user.status === "CREATED")
    );
};

const confirm = useConfirm();

/**
 * Shows a confirmation dialog before saving when the save would newly enable anonymity.
 */
const confirmSave = () => {
    // Warn only when anonymity is being turned on by this save — an already anonymous project
    // saves its other settings without ceremony. Cancelling just closes the dialog without
    // saving; the form keeps whatever the user selected.
    if (updatedProject.value.anonymous && !props.project.anonymous) {
        confirm.require({
            message: t("project-configuration.anonymous.warningMessage"),
            header: t("project-configuration.anonymous.input-label"),
            icon: "pi pi-exclamation-triangle",
            rejectClass: "p-button-secondary",
            rejectLabel: t("buttons.cancel"),
            acceptLabel: t("buttons.save"),
            accept: () => {
                updateProject();
            },
        });
    } else {
        updateProject();
    }
};

/**
 * Closes the project configuration (ProjectConfig component call) and resets the anonymous toggle if it was set to true
 */
const cancelProjectConfig = () => {
    if (!props.project.anonymous) {
        updatedProject.value.anonymous = false;
    }
    editMode.value = false;
};

watch(
    () => props.participants,
    (newParticipants) => {
        if (newParticipants) {
            // "Active" mirrors the backend: any participant that advanced beyond CREATED/REVOKED has
            // acknowledged the recipient, so it may no longer be freely changed.
            hasActiveParticipants.value = newParticipants.some(
                (participant) => !["CREATED", "REVOKED"].includes(participant.status ?? ""),
            );
            directLinkAmount.value = props.participants.length;
        }
    },
    { immediate: true },
);

/**
 * Calls the API to create a direct link for the project
 * @param {number} amount - amount of direct links to be created
 * @param {string | null} expiresAt - optional ISO instant at which the direct link is deleted automatically
 */
async function createDirectLink(amount: number, expiresAt: string | null = null) {
    try {
        const updatedProjectStatus = await directLinkStore.createDirectLink(
            updatedProject.value,
            amount,
            props.trainer.id,
            expiresAt,
        );
        emit("update:project", updatedProjectStatus);
    } catch (error) {
        console.error("ERROR creating direct link: ", error);
    }
}

function deleteProjectWithConfirmation() {
    confirm.require({
        message: t("project-details.deletion.message"),
        header: t("project-details.deletion.header"),
        rejectClass: "p-button-secondary",
        rejectLabel: t("buttons.cancel"),
        acceptLabel: t("buttons.delete"),
        accept: async () => {
            if (props.project.id === undefined) {
                return;
            }

            // profile-administration has profile-contractor delete the project's direct input and
            // calculations together with its other data (RDT-470).
            if (props.directLink) {
                await ProfileAdministrationAPI.deleteDirectLink(props.project.id);
            }

            await ProfileAdministrationAPI.deleteProject(props.project.id);
            emit("update:deleteProject", props.project.id);
        },
    });
}

function enableEditMode() {
    editMode.value = true;
}

async function updateProject() {
    if (valid.value && updatedProject.value) {
        await ProfileAdministrationAPI.updateProject(updatedProject.value);
        editMode.value = false;
        emit("update:project", updatedProject.value);
    }
}

/**
 * Updates the visibility of the project menu items based on the direct link status
 */
watch(
    () => props.directLink,
    (newDirectLink) => {
        projectMenuItems.find((item) => item.name === "createLinkItem")!.visible =
            !newDirectLink && supportsDirectLink();
        projectMenuItems.find((item) => item.name === "deleteLinkItem")!.visible = !!newDirectLink;
        projectMenuItems.find((item) => item.name === "editDirectLinkContingentItem")!.visible = !!newDirectLink;
    },
    { immediate: true },
);

onMounted(async () => {
    await directLinkStore.refreshAvailableQuantity(props.trainer.id, props.project.defaultProduct);
    directLinkAmount.value = props.participants.length;
});
</script>

<template>
    <ConfirmDialog></ConfirmDialog>
    <div v-if="editMode" class="mt-8">
        <ProjectConfig
            :product="product"
            :trainer="trainer"
            :project="projectToEdit"
            :has-active-participants="hasActiveParticipants"
            :has-direct-link="directLink !== null"
            :anonymous-switch-disabled="checkAnonymousSwitchDisabled()"
            @update:valid="updateValid"
            @update:project="updateFromConfig"
        />
        <div class="grid grid-cols-12 gap-4 mt-4">
            <div class="col-span-12 flex gap-2 justify-end">
                <Button :label="t('buttons.cancel')" @click="cancelProjectConfig" size="small" outlined></Button>
                <Button
                    :id="`project-details-menu_btn_save-${project.id}`"
                    :label="t('buttons.save')"
                    icon="pi pi-check"
                    @click="confirmSave"
                    size="small"
                    autofocus
                ></Button>
            </div>
        </div>
    </div>
    <div v-else>
        <div class="flex flex-wrap gap-4 mt-4">
            <ProjectDetailsEditMenu
                v-if="isMobileView()"
                :project-menu-items="projectMenuItems"
                :project="project"
                @update:enable-edit-mode="enableEditMode"
            />
            <div class="basis-full md:flex-1">
                <ProjectInformation
                    :project="project"
                    :direct-link="directLink"
                    :trainer="trainer"
                    :participants="participants"
                    :departments="departments"
                    :with-label="true"
                    :show-direct-link-info="false"
                />
                <div
                    v-if="updatedProject?.description && updatedProject.description.length > 0"
                    :id="`project-details-menu_div_description-${project.id}`"
                >
                    <i class="pi pi-align-left mr-2"></i>{{ updatedProject?.description }}
                </div>
            </div>
            <ProjectDetailsEditMenu
                v-if="!isMobileView()"
                :project-menu-items="projectMenuItems"
                :project="project"
                @update:enable-edit-mode="enableEditMode"
            />
        </div>
    </div>

    <!-- Direct Link handling -->
    <!-- Direct link creation/edit dialog -->
    <DirectLinkEditDialog
        v-if="directLinkCreationVisible"
        :participants="props.participants"
        :direct-link="props.directLink"
        :available-quantity="availableQuantity"
        create
        @create:directlink="createDirectLink"
        @close:dialog="directLinkStore.closeCreateDialog()"
    />

    <!-- Create direct link warning dialog -->
    <Dialog
        v-model:visible="createDirectLinkWarningDialogVisible"
        modal
        :header="t('project-configuration.directLink.create')"
        :style="{ width: '30rem' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <p class="p-text-secondary mb-8">
            <span v-html="t('project-configuration.directLinkWarning')" />
            <Tag :value="t('status.CREATED')" severity="secondary" class="mx-1" /><br><br>
            <span v-html="t('project-configuration.directLinkWarningP2')" />
        </p>
        <div class="flex justify-center gap-2">
            <Button
                type="button"
                :label="t('buttons.ok')"
                severity="primary"
                @click="directLinkStore.closeCreateWarning()"
            />
        </div>
    </Dialog>
</template>
